import { createTheme, Theme, PaletteOptions, lighten, darken } from '@mui/material/styles';
import { red, orange, grey } from '@mui/material/colors';
import { primaryColor } from "../../config/config";
import tooltipStyles from './components/tooltipStyles';
// Import Open Sans so it’s available globally
import '@fontsource/open-sans';

interface CustomPaletteOptions extends PaletteOptions {
  tertiary?: PaletteOptions['primary'];
  quaternary?: PaletteOptions['primary'];
  customText?: {
    logo?: string;
    headings?: string;
  };
}

// Extend the default theme to include your custom fields
declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true; // Add custom breakpoint
    xxxl: true; // Add custom breakpoint
  }
  interface Palette {
    tertiary: Palette['primary'];
    quaternary: Palette['primary'];
    colors: {
      red: string;
      yellow: string;
      green: string;
      purple: string;
      plotColor: string;
      plotHighlights: string;
      plotSecondayColor: string;
      plotplotHighlightsSecondayColor: string;
      plotSnowLayerColor: string;
      plotIceLayerColor: string;
      plotWaterLayerColor: string;
      plotSurfaceGripColor: string;
      plotYellow: string;
      plotBlue: string;
      plotOrange: string;
      aiGreen: string;
    }
    customText: {
      logo: string;
      headings: string;
      logoInvert: string;
      logoColored: string;
      headerColor: string;
      headingsGrayedOut: string;
    };
    switch: {
      checked: string;
      track: string;
      icon: string;
      iconDark: string;
    };
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary'];
    quaternary?: PaletteOptions['primary'];
    colors?: {
      red?: string;
      yellow?: string;
      green?: string;
      purple?: string;
      plotColor?: string;
      plotHighlights?: string;
      plotSecondayColor?: string;
      plotplotHighlightsSecondayColor?: string;
      plotSnowLayerColor?: string;
      plotIceLayerColor?: string;
      plotWaterLayerColor?: string;
      plotSurfaceGripColor?: string;
      plotYellow?: string;
      plotBlue?: string;
      plotOrange?: string;
      aiGreen?: string;
    }
    customText?: {
      logo?: string;
      headings?: string;
      logoInvert?: string;
      logoColored?: string;
      headerColor?: string;
      headingsGrayedOut?: string;
    };
    switch: {
      checked: string;
      track: string;
      icon: string;
      iconDark: string;
    };
  }

  // Extend the TypeBackground interface to include the custom sidebarBackground, sidebarActive, sidebarHover, sidebarText, and headerBackground properties
  interface TypeBackground {
    paper: string;
    sidebarBackground: string;
    sidebarActive: string;
    sidebarHover: string;
    sidebarText: string;
    headerBackground: string;
  }
}

export const getTheme = (darkMode: 'dark' | 'light'): Theme => {
  return createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
        xxl: 1900, // Custom breakpoint for very large screens
        xxxl: 2200, // Custom breakpoint for very large screens
      },
    },
    palette: {
      mode: darkMode,
      primary: {
        main: primaryColor,
        light: lighten(primaryColor, 0.2),
        dark: darken(primaryColor, 0.25),
      },
      secondary: {
        main: '#000',
        light: '#000',
      },
      tertiary: {
        main: '#78D4D7',
        light: '#000',
        dark: '#78D4D7',
      },
      quaternary: {
        main: '#fff',
        light: '#fff',
        dark: '#000',
      },
      text: {
        primary: darkMode === 'dark' ? '#fff' : '#000',
        secondary: darkMode === 'dark' ? '#fff' : '#000',
      },
      colors: {
        red: '#FF2525',
        yellow: '#F4AF00',
        green: '#76B82A',
        purple: '#860ffe',
        plotColor: darkMode === 'dark' ? primaryColor : primaryColor,
        plotHighlights: darkMode === 'dark' ? 'rgba(190, 214, 0,0.5)' : 'rgba(190, 214, 0,0.2)',
        plotSecondayColor: darkMode === 'dark' ? '#1f80b2' : '#1f80b2',
        plotplotHighlightsSecondayColor: darkMode === 'dark' ? 'rgba(31, 128, 178,0.5)' : 'rgba(31, 128, 178,0.2)',
        plotSnowLayerColor: '#FFD700',
        plotIceLayerColor: '#0077B6',
        plotWaterLayerColor: '#00A3D7',
        plotSurfaceGripColor: '#32CD32',
        plotBlue: "#00C2FF",
        plotOrange: "#DF9B15",
        aiGreen: "#BED600",
      },
      customText: {
        headings: darkMode === 'dark' ? '#FFF' : '#000',
        headingsGrayedOut: darkMode === 'dark' ? "rgba(255, 255, 255, .3)" : "rgba(33, 37, 41, .3)",
        logo: darkMode === 'dark' ? primaryColor : primaryColor,
        logoInvert: darkMode === 'dark' ? '#000' : '#fff',
        logoColored: darkMode === 'dark' ? primaryColor : '#fff',
        headerColor: "#000",
      },
      switch: {
        checked: darkMode === 'dark' ? "white" : primaryColor,
        track: darkMode === 'dark' ? primaryColor : "white",
        icon: darkMode === 'dark' ? "white" : primaryColor,
        iconDark: darkMode === 'dark' ? "white" : primaryColor,
      },
      contrastThreshold: 3,
      tonalOffset: 0.2,
      background: {
        paper: darkMode === 'dark' ? '#000' : '#FFFFFF',
        sidebarBackground: darkMode === 'dark' ? "rgba(33, 37, 41, 1)" : "rgba(255, 255, 255,1)",
        sidebarActive: primaryColor,
        sidebarHover: darkMode === 'dark'
          ? darken(primaryColor, 0.6)
          : lighten(primaryColor, 0.6),
        sidebarText: darkMode === 'dark' ? "#F5F5F5" : '#000',
        headerBackground: '#F5F5F5',
      },
      error: {
        main: '#D40101',
        light: red[50],
      },
      warning: {
        main: primaryColor,
        light: orange[50],
      },
      grey: grey,
    },
    spacing: 8,
    components: {
      // Global override: ensures the default font is Open Sans everywhere via CssBaseline
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: "'Open Sans', sans-serif",
          },
        },
      },
      ...tooltipStyles(darkMode),
      MuiButton: {
        styleOverrides: {
          root: {
            color: darkMode === 'dark' ? '#fff' : '#000',
            '&:hover': {
              backgroundColor: darkMode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.04)',
            },
            '&:active': {
              boxShadow: 'inset 0 3px 5px rgba(0, 0, 0, 0.125)',
            },
          },
          contained: {
            backgroundColor: darkMode === 'dark' ? '#333' : '#eee',
            '&:hover': {
              backgroundColor: darkMode === 'dark' ? '#444' : '#ddd',
            },
          },
          outlined: {
            borderColor: darkMode === 'dark' ? '#fff' : '#000',
            '&:hover': {
              backgroundColor: darkMode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.04)',
            },
          },
          text: {
            '&:hover': {
              backgroundColor: darkMode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.06)',
            },
          }
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            width: '10rem',
            paddingTop: '8px',
            border: "none",
            borderBottom: `1px solid ${darkMode === 'dark' ? '#fff' : '#000'}`,
            '&$focused': {
              borderColor: 'transparent',
            },
            '&$error': {
              boxShadow: `0 0 0 2px ${red[500]}`,
            },
          }
        }
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            marginBottom: '8px',
            '&$focused $notchedOutline': {
              borderColor: 'transparent',
            },
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderRadius: '8px',
            backgroundColor: darkMode === 'dark' ? '#143746E5' : '#fff',
            border: `1px solid ${darkMode === 'dark' ? '#143746E5' : '#ddd'}`,
          }
        }
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            border: 'none',
            borderWidth: '0px',
            color: darkMode === 'dark' ? '#fff' : '#000',
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-columnSeparator': {
              visibility: 'hidden',
            },
            '& .MuiDataGrid-columnHeaders': {
              fontSize: '1rem',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell': {
              fontSize: '0.9rem',
              fontWeight: 400,
              borderBottomColor: darkMode === 'dark' ? '#fff' : '#ddd',
            },
            '& .MuiDataGrid-row': {
              maxHeight: '3rem !important',
              minHeight: '3rem !important',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              '& .MuiDataGrid-row': {
                maxHeight: '3rem !important',
                minHeight: '3rem !important',
              }
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.9rem',
              fontWeight: 400,
            },
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.9rem',
              fontWeight: 400,
            },
          }
        }
      },
      MuiTabs: {
        styleOverrides: {
          root: {
            position: 'absolute',
            right: '1rem',
            padding: '0',
            minHeight: '16px',
            top: "1rem",
            border: `1px solid ${darkMode === 'dark' ? '#fff' : '#000'}`,
            borderRadius: '8px',
            '& .MuiTabs-indicator': {
              backgroundColor: 'transparent'
            },
          }
        }
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: "12px",
            minHeight: '24px',
            padding: '3px 6px',
            color: darkMode === 'dark' ? '#888' : '#888',
            '&:hover': {
              backgroundColor: darkMode === 'dark' ? '#333' : '#eee',
            },
            '&.Mui-selected': {
              color: darkMode === 'dark' ? '#fff' : '#000',
            },
            '&:not(:last-child)': {
              borderRight: `1px solid ${darkMode === 'dark' ? '#fff' : '#000'}`,
            }
          }
        }
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before, &:after': {
              display: 'none',
            },
            '&:hover:not(.Mui-disabled):before': {
              display: 'none',
            }
          }
        }
      }
    },
    typography: {
      // Set Open Sans as the global font family for all typography elements
      fontFamily: "'Open Sans', sans-serif",
      h1: {
        fontSize: '24px',
        fontWeight: 'semi-bold',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      h2: {
        fontSize: '18px',
        fontWeight: 'semi-bold',
        textTransform: 'uppercase',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      h3: {
        fontSize: '18px',
        fontWeight: 'normal',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      h4: {
        fontSize: '12px',
        fontWeight: '300',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      h5: {
        fontSize: '12px',
        fontWeight: 'normal',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      h6: {
        fontSize: '24px',
        fontWeight: 'bold',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      button: {
        fontSize: '14px',
        fontWeight: 'semi-bold',
        letterSpacing: '1px',
        lineHeight: '24px',
        color: darkMode === 'dark' ? '#FFFFFFF2' : '#000',
      },
      body1: {
        fontSize: '12px',
        fontWeight: 'normal',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      body2: {
        fontSize: '20px',
        fontWeight: '600',
        textEdge: "cap",
        color: darkMode === 'dark' ? '#FFF' : '#000',
      },
      caption: {
        fontSize: '18px',
        fontWeight: '300',
        color: darkMode === 'dark' ? '#FFF' : '#000',
      }
    },
  });
};
